/**
 * sophior-b2b-service.js: Service voor Retailers (klantcases).
 * Laadt rol + bedrijven via GET /me, cases via GET /cases, dient in via
 * POST /cases en (admin) werkt bij via PATCH /cases/:id. Alle data komt via de
 * Shopify app proxy (data-proxy-url); de backend bepaalt rol en bedrijf. Rijen
 * worden met createElement + textContent gebouwd, nooit met innerHTML.
 *
 * Designbeslissingen (spec hoofdstuk 5.6):
 * - De statusregio is alleen voor resultaten van handelingen en fouten. De
 *   laadstatus gebruikt het lege-vlak dat er toch al staat, zodat er tijdens
 *   het laden niets verschuift (CLS).
 * - Na elke response van indienen of opslaan krijgt de statusregio focus: het
 *   formulier staat boven de tabel, maar op mobiel of bij een lange lijst kan
 *   de melding anders buiten beeld vallen.
 * - Een nieuwe case wordt client-side bovenaan ingevoegd, nooit met een verse
 *   GET /cases: de zoekindex van Shopify loopt daar even op achter.
 * - Admin-rijen hebben een dirty-state: Opslaan is disabled tot er echt iets
 *   is gewijzigd, en een jaar- of bedrijfswissel met openstaande wijzigingen
 *   wordt de eerste keer teruggedraaid met een melding.
 * - Bij een sessiefout (401) krijgt de melding een inloglink mee.
 * - In de Theme Editor gaat er nooit een verzoek uit: indienen en opslaan
 *   tonen daar alleen een voorbeeldmelding, zodat JP de statusregio kan stylen.
 * - Meldingen die uit een sectie-setting komen hebben een terugval, zodat een
 *   leeggemaakte setting nooit een knop stil laat falen.
 */
(function () {
  var root = document.querySelector('[data-b2b-service]');
  if (!root) return;

  /* proxyUrl alleen overnemen als het een relatief app-proxy-pad is; een Theme
     Editor-waarde als absolute externe URL zou company-hint en redentekst naar
     een ander domein sturen */
  var proxyUrl = /^\/apps\/[a-z0-9-]+$/.test(root.dataset.proxyUrl || '') ? root.dataset.proxyUrl : '/apps/b2b-service';
  var designMode = root.dataset.designMode === 'true';
  var huidigJaar = parseInt(root.dataset.jaar, 10) || new Date().getFullYear();
  var startJaar = parseInt(root.dataset.startJaar, 10) || huidigJaar;
  var hintCompany = root.dataset.companyId || '';
  /* customer.current_company.id is normaal numeriek en de backend werkt met
     GID's: overal op hintGid vergelijken. Levert Liquid toch al een GID, dan
     zou opnieuw voorvoegen een waarde geven die nooit matcht: het bedrijf wordt
     dan stil verkeerd voorgeselecteerd. Daarom eerst controleren. */
  var hintGid = hintCompany
    ? (hintCompany.indexOf('gid://') === 0 ? hintCompany : 'gid://shopify/Company/' + hintCompany)
    : '';
  var loginUrl = root.dataset.loginUrl || '';

  var statusEl = root.querySelector('[data-service-status]');
  var adminBlok = root.querySelector('[data-service-admin]');
  var companySelect = root.querySelector('[data-service-company-select]');
  var companyNameEl = root.querySelector('[data-service-company-name]'); /* in het beheerblok ("Cases van"), NIET de "Ingelogd als"-regel */
  var adminCompanyLine = root.querySelector('[data-service-admin-company]');
  var yearSelect = root.querySelector('[data-service-year-select]');
  var casesSection = root.querySelector('[data-service-cases]');
  var table = root.querySelector('[data-service-table]');
  var rowsEl = root.querySelector('[data-service-rows]');
  var emptyEl = root.querySelector('[data-service-empty]');
  var form = root.querySelector('[data-service-form]');
  var productSelect = root.querySelector('[data-service-product]');
  var reasonEl = root.querySelector('[data-service-reason]');
  var submitBtn = root.querySelector('[data-service-submit]');
  var captionCompanyEl = root.querySelector('[data-service-caption-company]');

  /* Zonder deze twee is er niets te renderen en zouden de uitlezingen hieronder
     stukvallen; de rest van de elementen is per gebruik null-gegard */
  if (!rowsEl || !reasonEl) return;

  var labels = {
    loading: root.dataset.msgLoading || 'Cases laden...',
    empty: root.dataset.msgEmpty || 'Nog geen cases in [jaar].',
    submitting: root.dataset.msgSubmitting || 'Indienen...',
    success: root.dataset.msgSuccess || 'Case #[nummer] is ingediend.',
    error: root.dataset.msgError || 'Indienen is niet gelukt, probeer het opnieuw of neem contact op.',
    loadError: root.dataset.msgLoadError || 'Cases laden is niet gelukt. Laad de pagina opnieuw.',
    limit: root.dataset.msgLimit || '',
    access: root.dataset.msgAccess || '',
    session: root.dataset.msgSession || '',
    validation: root.dataset.msgValidation || '',
    noCompany: root.dataset.msgNoCompany || '',
    saved: root.dataset.msgSaved || 'Case #[nummer] bijgewerkt.',
    unsaved: root.dataset.msgUnsaved || '',
    yes: root.dataset.labelYes || 'Ja',
    no: root.dataset.labelNo || 'Nee',
    order: root.dataset.labelOrder || 'Order',
    save: root.dataset.labelSave || 'Opslaan',
    done: root.dataset.labelDone || 'Afgehandeld',
    ordernumber: root.dataset.labelOrdernumber || 'Ordernummer',
    login: root.dataset.labelLogin || 'Inloggen',
    skuPrefix: root.dataset.skuPrefix || 'Art.nr.'
  };
  var kolom = {
    kase: rowsEl.dataset.labelCase, product: rowsEl.dataset.labelProduct, reden: rowsEl.dataset.labelReason,
    aangemaakt: rowsEl.dataset.labelCreated, afgehandeld: rowsEl.dataset.labelDone
  };

  var state = {
    rol: 'klant', bedrijven: [], company: '', jaar: huidigJaar, bezig: false,
    redenMin: parseInt(reasonEl.getAttribute('minlength'), 10) || 5,
    redenMax: parseInt(reasonEl.getAttribute('maxlength'), 10) || 1000,
    wisselBevestigd: false, /* dirty-guard bij jaar-/bedrijfswissel */
    meFout: null, /* mislukte /me- of /companies-response: verklaart een leeg state.company */
    formulierUit: false, /* vangnet zonder bedrijf: het formulier blijft uit, ook na een save */
    submitLabel: submitBtn ? submitBtn.textContent : ''
  };

  /* Theme Editor: vaste voorbeelddata, geen netwerk */
  var voorbeeldCases = [
    { id: '0', casenummer: 2, jaar: huidigJaar, product: { titel: 'Walnoot snijplank', variant: '40 x 30 cm', sku: 'SN-WAL-4030' }, reden: 'Plank gebarsten na twee weken gebruik', aangemaakt: new Date().toISOString(), afgehandeld: true, ordernummer: '#1042' },
    { id: '0', casenummer: 1, jaar: huidigJaar, product: { titel: 'Koekenpan 28 cm', variant: '', sku: 'PA-KP-28' }, reden: 'Handvat zit los', aangemaakt: new Date().toISOString(), afgehandeld: false, ordernummer: '' }
  ];

  var SESSIE_CODES = ['NIET_INGELOGD', 'HANDTEKENING_ONGELDIG', 'TIMESTAMP_VEROUDERD'];

  /* Ondergrens van de servervalidatie (valideerJaar, spec 3.8.3): een ouder
     jaar geeft 400 ONGELDIGE_INVOER, dus zulke jaren horen niet in de keuze */
  var MIN_JAAR = 2020;

  /* ---- Status en focus ---- */

  /**
   * Statusmelding tonen of wissen. Met linkHref komt er een inloglink achter
   * de tekst (sessie verlopen); verder is de regio textContent-only.
   */
  function setStatus(tekst, isFout, linkHref) {
    if (!statusEl) return;
    /* Regio blijft altijd in de DOM en accessibility tree (geen hidden):
       anders wordt de melding door screenreaders niet aangekondigd */
    statusEl.textContent = tekst || '';
    statusEl.classList.toggle('sophior-b2b__status--zichtbaar', !!tekst);
    statusEl.classList.toggle('sophior-b2b__status--fout', !!tekst && !!isFout);
    if (tekst && linkHref) {
      statusEl.appendChild(document.createTextNode(' '));
      var link = document.createElement('a');
      link.href = linkHref;
      link.textContent = labels.login;
      statusEl.appendChild(link);
    }
  }

  /* De melding in beeld scrollen en laten voorlezen; tabindex=-1 staat in de markup */
  function focusStatus() {
    if (!statusEl || !statusEl.textContent) return;
    statusEl.focus({ preventScroll: false });
  }

  /* ---- Netwerk ---- */

  /**
   * Eén proxy-call. Altijd eerst JSON parsen en pas daarna op r.ok vertakken,
   * zodat de foutcode uit de body beschikbaar is. Geeft bij een niet-JSON
   * antwoord (bijv. de Shopify-404-pagina als de app proxy ontbreekt) data null.
   */
  function api(method, path, body) {
    var opties = {
      method: method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    };
    if (body) {
      /* De Worker eist Content-Type application/json op POST/PATCH (415 zonder) */
      opties.headers['Content-Type'] = 'application/json';
      opties.body = JSON.stringify(body);
    }
    return fetch(proxyUrl + path, opties).then(function (r) {
      return r.json().then(
        function (data) { return { ok: r.ok, status: r.status, data: data }; },
        function () { return { ok: false, status: r.status, data: null }; }
      );
    });
  }

  /* Foutcode uit de response, null-veilig */
  function foutCode(res) {
    return (res && res.data && res.data.error && res.data.error.code) || '';
  }

  function isSessieFout(res) {
    var code = foutCode(res);
    for (var i = 0; i < SESSIE_CODES.length; i++) {
      if (SESSIE_CODES[i] === code) return true;
    }
    return false;
  }

  /**
   * Nederlandse tekst bij een foutresponse. context is 'load', 'submit' of
   * 'save'; de teksten komen uit de section-settings (data-attributen).
   */
  function foutTekst(res, context) {
    var fout = res && res.data && res.data.error;
    var code = (fout && fout.code) || '';
    var msg = (fout && fout.message) || '';
    var terugval = context === 'load' ? labels.loadError : labels.error;
    /* LIMIET_BEREIKT dekt twee gevallen: de daglimiet per bedrijf (alleen bij
       POST /cases, dus context 'submit') en de rate limit van 30 verzoeken per
       minuut (elke route). Buiten submit is de daglimiettekst dus feitelijk
       onjuist en gaat de Worker-tekst voor. */
    if (code === 'LIMIET_BEREIKT') {
      return context === 'submit' ? (labels.limit || msg || terugval) : (msg || labels.limit || terugval);
    }
    if (code === 'GEEN_TOEGANG') return labels.access || msg || terugval;
    if (code === 'GEEN_BEDRIJF') return labels.noCompany || msg || terugval;
    if (isSessieFout(res)) return labels.session || msg || terugval;
    if (code === 'ONGELDIGE_INVOER' && context === 'submit') {
      return (labels.validation || msg || terugval).replace('[min]', state.redenMin);
    }
    return msg || terugval;
  }

  /* ---- Toestand van de bedieningselementen ---- */

  /**
   * De bedieningselementen in of uit de bezig-stand. alleenRijen is de
   * save-variant (onSave): dan blijven het productveld, de reden en het
   * opschrift van de indienknop met rust, want er wordt niets ingediend en
   * "Indienen..." op de primaire knop zou dan onjuist zijn. De knop zelf gaat
   * wel op disabled: een klik erop wordt tijdens een save toch genegeerd.
   */
  function setBusy(busy, alleenRijen) {
    state.bezig = busy;
    if (!alleenRijen) {
      if (productSelect) productSelect.disabled = busy || state.formulierUit;
      reasonEl.disabled = busy || state.formulierUit;
    }
    if (yearSelect) yearSelect.disabled = busy;
    if (companySelect) companySelect.disabled = busy;
    if (submitBtn) {
      submitBtn.disabled = busy || state.formulierUit;
      if (!alleenRijen) submitBtn.textContent = busy ? labels.submitting : state.submitLabel;
    }
    var velden = rowsEl.querySelectorAll('[data-service-done], [data-service-order]');
    for (var i = 0; i < velden.length; i++) velden[i].disabled = busy;
    /* Opslaan staat tijdens busy hard uit en gaat daarna terug naar de
       dirty-state van de eigen rij (een schone rij blijft disabled) */
    var knoppen = rowsEl.querySelectorAll('[data-service-save]');
    for (var j = 0; j < knoppen.length; j++) {
      var rij = knoppen[j].closest('[data-service-row]');
      knoppen[j].disabled = busy || !rij || rij.dataset.dirty !== 'true';
    }
  }

  /* Vangnet als er geen bedrijf aan het account hangt: indienen kan dan niet */
  function zetFormulierUit() {
    state.formulierUit = true; /* zodat een setBusy(false) het formulier niet alsnog aanzet */
    if (productSelect) productSelect.disabled = true;
    reasonEl.disabled = true;
    if (submitBtn) submitBtn.disabled = true;
  }

  /**
   * Jaarkeuze vullen, aflopend. /me.jaar is leidend (Amsterdam-jaar uit de
   * Worker); huidigJaar uit Liquid is alleen de startwaarde vóór /me en de
   * waarde in de Theme Editor.
   */
  function fillYears() {
    if (!yearSelect) return;
    var boven = Math.max(state.jaar, huidigJaar);
    /* Ondergrens nooit boven het gekozen jaar: anders staat het actieve jaar niet in de lijst */
    var onder = Math.min(startJaar, state.jaar);
    /* En nooit onder het serverminimum: de sectie-setting start_jaar kent geen
       grens, terwijl elk jaar voor MIN_JAAR een 400 uit de Worker oplevert */
    if (onder < MIN_JAAR) onder = Math.min(MIN_JAAR, state.jaar);
    yearSelect.textContent = '';
    for (var jaar = boven; jaar >= onder; jaar--) {
      var optie = document.createElement('option');
      optie.value = String(jaar);
      optie.textContent = String(jaar);
      if (jaar === state.jaar) optie.selected = true;
      yearSelect.appendChild(optie);
    }
    yearSelect.value = String(state.jaar);
  }

  function formatDate(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso).slice(0, 10);
      return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return String(iso).slice(0, 10);
    }
  }

  /* Laadstatus in het lege-vlak dat er toch al staat: geen layoutverschuiving */
  function setLoading(aan) {
    if (casesSection) casesSection.setAttribute('aria-busy', aan ? 'true' : 'false');
    if (!aan) return;
    if (emptyEl) {
      emptyEl.textContent = labels.loading;
      emptyEl.hidden = false;
    }
    if (table) table.hidden = true;
  }

  /* Laden afsluiten op elk pad dat loadCases overslaat. De sectie rendert
     statisch al in de laadtoestand (laadtekst zichtbaar, tabel hidden, §5.2),
     dus zonder deze aanroep blijft "Cases laden..." naast de foutmelding staan.
     Zelfde afhandeling als de foutpaden van loadCases. */
  function toonGeenCases() {
    setLoading(false);
    renderRows([]);
  }

  function setAdminCompanyName(naam) {
    if (companyNameEl) companyNameEl.textContent = naam;
    if (adminCompanyLine) adminCompanyLine.hidden = !naam;
    if (captionCompanyEl) captionCompanyEl.textContent = naam;
    /* De "Ingelogd als"-regel in de header blijft ongemoeid: die toont altijd
       het eigen bedrijf van de ingelogde gebruiker */
  }

  /* ---- Rijen bouwen (createElement + textContent, nooit innerHTML) ---- */

  function maakCel(tag, soort, label) {
    var cel = document.createElement(tag);
    cel.className = 'sophior-b2b__cel sophior-b2b__cel--' + soort;
    cel.setAttribute('role', tag === 'th' ? 'rowheader' : 'cell');
    if (tag === 'th') cel.setAttribute('scope', 'row');
    /* data-label voedt het mobiele kaartlabel via CSS content: attr(data-label) */
    if (label) cel.setAttribute('data-label', label);
    return cel;
  }

  /* Klantweergave: tekstlabel Ja/Nee (geen pill) plus het ordernummer */
  function maakKlantCel(c) {
    var cel = maakCel('td', 'afgehandeld', kolom.afgehandeld);
    var label = document.createElement('span');
    label.className = 'sophior-b2b__case-label sophior-b2b__case-label--' + (c.afgehandeld ? 'ja' : 'nee');
    label.textContent = c.afgehandeld ? labels.yes : labels.no;
    cel.appendChild(label);
    if (c.ordernummer) {
      var order = document.createElement('span');
      order.className = 'sophior-b2b__case-order';
      order.textContent = labels.order + ' ' + c.ordernummer;
      cel.appendChild(order);
    }
    return cel;
  }

  /* Adminweergave: checkbox, ordernummer en Opslaan. Bewust GEEN data-label:
     het checkbox-label is op mobiel al de kop van dit blok. */
  function maakAdminCel(c, tr) {
    var cel = maakCel('td', 'afgehandeld', '');
    var wrap = document.createElement('div');
    wrap.className = 'sophior-b2b__case-admin';

    var checkLabel = document.createElement('label');
    checkLabel.className = 'sophior-b2b__check';
    var check = document.createElement('input');
    check.type = 'checkbox';
    check.checked = !!c.afgehandeld;
    check.setAttribute('data-service-done', '');
    var checkTekst = document.createElement('span');
    checkTekst.textContent = labels.done;
    checkLabel.appendChild(check);
    checkLabel.appendChild(document.createTextNode(' '));
    checkLabel.appendChild(checkTekst);
    wrap.appendChild(checkLabel);

    var orderLabel = document.createElement('label');
    orderLabel.className = 'sophior-b2b__veld sophior-b2b__veld--klein';
    var orderTekst = document.createElement('span');
    orderTekst.className = 'sophior-b2b__label';
    orderTekst.textContent = labels.ordernumber;
    var orderInput = document.createElement('input');
    orderInput.type = 'text';
    orderInput.className = 'sophior-b2b__input';
    orderInput.maxLength = 40;
    orderInput.value = c.ordernummer || '';
    orderInput.setAttribute('data-service-order', '');
    orderLabel.appendChild(orderTekst);
    orderLabel.appendChild(orderInput);
    wrap.appendChild(orderLabel);

    var knop = document.createElement('button');
    knop.type = 'button';
    knop.className = 'sophior-b2b__knop sophior-b2b__knop--secundair sophior-b2b__knop--klein';
    knop.setAttribute('data-service-save', '');
    knop.textContent = labels.save;
    knop.setAttribute('aria-label', labels.save + ', case #' + c.casenummer);
    /* Dirty-state: pas klikbaar als er echt iets is gewijzigd */
    knop.disabled = true;
    wrap.appendChild(knop);

    function markeerDirty() {
      tr.dataset.dirty = 'true';
      knop.disabled = state.bezig;
    }
    check.addEventListener('change', markeerDirty);
    orderInput.addEventListener('input', markeerDirty);
    orderInput.addEventListener('change', markeerDirty);
    knop.addEventListener('click', function () {
      onSave(tr);
    });

    cel.appendChild(wrap);
    return cel;
  }

  function renderRow(c) {
    var tr = document.createElement('tr');
    tr.setAttribute('role', 'row');
    tr.className = 'sophior-b2b__rij sophior-b2b__rij--case';
    tr.setAttribute('data-service-row', '');
    tr.dataset.caseId = c.id == null ? '' : String(c.id);
    tr.dataset.caseNummer = c.casenummer == null ? '' : String(c.casenummer);

    var caseCel = maakCel('th', 'case', kolom.kase);
    caseCel.textContent = '#' + c.casenummer;
    tr.appendChild(caseCel);

    var product = c.product || {};
    var productCel = maakCel('td', 'product', kolom.product);
    var productTekst = document.createElement('span');
    productTekst.className = 'sophior-b2b__product-tekst';
    var naam = document.createElement('span');
    naam.className = 'sophior-b2b__product-naam';
    naam.textContent = product.titel || '';
    productTekst.appendChild(naam);
    if (product.variant) {
      var variant = document.createElement('span');
      variant.className = 'sophior-b2b__product-variant';
      variant.textContent = product.variant;
      productTekst.appendChild(variant);
    }
    if (product.sku) {
      var meta = document.createElement('span');
      meta.className = 'sophior-b2b__product-meta';
      var sku = document.createElement('span');
      sku.className = 'sophior-b2b__product-sku';
      sku.textContent = labels.skuPrefix + ' ' + product.sku;
      meta.appendChild(sku);
      productTekst.appendChild(meta);
    }
    productCel.appendChild(productTekst);
    tr.appendChild(productCel);

    var redenCel = maakCel('td', 'reden', kolom.reden);
    redenCel.textContent = c.reden || '';
    tr.appendChild(redenCel);

    var datumCel = maakCel('td', 'aangemaakt', kolom.aangemaakt);
    var tijd = document.createElement('time');
    if (c.aangemaakt) {
      tijd.setAttribute('datetime', c.aangemaakt);
      tijd.textContent = formatDate(c.aangemaakt);
    }
    datumCel.appendChild(tijd);
    tr.appendChild(datumCel);

    tr.appendChild(state.rol === 'admin' ? maakAdminCel(c, tr) : maakKlantCel(c));
    return tr;
  }

  function renderRows(cases) {
    var lijst = cases || [];
    rowsEl.textContent = '';
    for (var i = 0; i < lijst.length; i++) rowsEl.appendChild(renderRow(lijst[i]));
    if (emptyEl) {
      emptyEl.textContent = lijst.length ? '' : labels.empty.replace('[jaar]', state.jaar);
      emptyEl.hidden = lijst.length > 0;
    }
    if (table) table.hidden = lijst.length === 0;
  }

  function heeftDirtyRijen() {
    return !!rowsEl.querySelector('[data-service-row][data-dirty="true"]');
  }

  /* ---- Laden ---- */

  function loadMe() {
    return api('GET', '/me')
      .then(function (res) {
        if (!res.ok) {
          /* Onthouden waarom /me faalde: zonder rol en bedrijf is elke latere
             melding over "geen bedrijf" misleidend (meestal is het de sessie) */
          state.meFout = res;
          toonGeenCases();
          setStatus(foutTekst(res, 'load'), true, isSessieFout(res) ? loginUrl : '');
          return;
        }
        var data = res.data || {};
        state.rol = data.rol === 'admin' ? 'admin' : 'klant';
        state.bedrijven = data.bedrijven || [];
        var jaar = parseInt(data.jaar, 10);
        if (jaar) state.jaar = jaar;
        var min = parseInt(data.reden_min, 10);
        if (min) {
          state.redenMin = min;
          reasonEl.minLength = min;
        }
        var max = parseInt(data.reden_max, 10);
        if (max) {
          state.redenMax = max;
          reasonEl.maxLength = max;
        }
        fillYears();
        if (state.rol === 'admin') {
          if (adminBlok) adminBlok.hidden = false;
          return loadCompanies();
        }
        /* Klant: de hint uit Liquid alleen gebruiken als de backend hem bevestigt */
        var gekozen = '';
        for (var i = 0; i < state.bedrijven.length; i++) {
          if (state.bedrijven[i] && state.bedrijven[i].id === hintGid) {
            gekozen = hintGid;
            break;
          }
        }
        if (!gekozen && state.bedrijven.length && state.bedrijven[0]) gekozen = state.bedrijven[0].id;
        state.company = gekozen || '';
        if (!state.company) {
          /* Vangnet: normaliter houdt de themagate customer.b2b? zo'n account al buiten de pagina.
             De terugval op labels.error houdt de melding zichtbaar als JP de
             sectie-setting error_no_company leeg maakt */
          toonGeenCases();
          setStatus(labels.noCompany || labels.error, true);
          zetFormulierUit();
        }
      })
      .catch(function () {
        state.meFout = { ok: false, status: 0, data: null };
        toonGeenCases();
        setStatus(foutTekst(null, 'load'), true);
      });
  }

  function loadCompanies() {
    return api('GET', '/companies')
      .then(function (res) {
        if (!res.ok) {
          state.meFout = res;
          toonGeenCases();
          setStatus(foutTekst(res, 'load'), true, isSessieFout(res) ? loginUrl : '');
          return;
        }
        var lijst = (res.data && res.data.bedrijven) || [];
        var geldig = [];
        for (var i = 0; i < lijst.length; i++) {
          if (lijst[i] && lijst[i].id) geldig.push(lijst[i]);
        }
        if (companySelect) companySelect.textContent = '';
        var gekozen = null;
        for (var j = 0; j < geldig.length; j++) {
          if (companySelect) {
            var optie = document.createElement('option');
            optie.value = geldig[j].id;
            optie.textContent = geldig[j].naam || '';
            companySelect.appendChild(optie);
          }
          /* Het eigen bedrijf van de admin vooraf selecteren als het in de lijst staat */
          if (geldig[j].id === hintGid) gekozen = geldig[j];
        }
        if (!gekozen && geldig.length) gekozen = geldig[0];
        state.company = gekozen ? gekozen.id : '';
        if (companySelect && state.company) companySelect.value = state.company;
        setAdminCompanyName(gekozen ? gekozen.naam || '' : '');
        if (!state.company) {
          /* Admin zonder bedrijven: zelfde behandeling als een klant zonder
             bedrijf. Zonder dit blijft de laadtekst staan en komt er nooit een
             tabel of lege-lijstmelding (loadCases wordt overgeslagen) */
          toonGeenCases();
          setStatus(labels.noCompany || labels.loadError, true);
          zetFormulierUit();
        }
      })
      .catch(function () {
        state.meFout = { ok: false, status: 0, data: null };
        toonGeenCases();
        setStatus(foutTekst(null, 'load'), true);
      });
  }

  /* Wordt nooit aangeroepen zonder state.company */
  function loadCases() {
    if (!state.company) return;
    setLoading(true);
    return api('GET', '/cases?company=' + encodeURIComponent(state.company) + '&year=' + encodeURIComponent(state.jaar))
      .then(function (res) {
        setLoading(false);
        if (!res.ok) {
          renderRows([]);
          setStatus(foutTekst(res, 'load'), true, isSessieFout(res) ? loginUrl : '');
          return;
        }
        renderRows((res.data && res.data.cases) || []);
      })
      .catch(function () {
        setLoading(false);
        renderRows([]);
        setStatus(foutTekst(null, 'load'), true);
      });
  }

  /* ---- Handelingen ---- */

  function onSubmit(e) {
    e.preventDefault();
    /* Theme Editor: nooit netwerk (spec 5.6). De voorbeeldrijen dragen id '0',
       en zonder klantsessie zou een echte POST alleen een 401 opleveren. De
       voorbeeldmelding laat JP de opmaak van de statusregio wel zien. */
    if (designMode) {
      setStatus(labels.success.replace('[nummer]', voorbeeldCases[0].casenummer + 1));
      return;
    }
    if (state.bezig) return;

    var variantGid = productSelect ? productSelect.value : '';
    var reden = reasonEl.value.trim();
    if (!variantGid || reden.length < state.redenMin) {
      /* Terugval op labels.error: een lege sectie-setting mag de knop niet stil laten falen */
      setStatus((labels.validation || labels.error).replace('[min]', state.redenMin), true);
      focusStatus();
      /* Daarna naar het veld dat nog aandacht vraagt */
      if (!variantGid && productSelect) {
        productSelect.focus();
      } else {
        reasonEl.focus();
      }
      return;
    }
    if (!state.company) {
      /* Kwam /me of /companies niet door, dan is de oorzaak daar bekend
         (meestal de sessie): "geen bedrijf gekoppeld" zou hier misleiden */
      if (state.meFout) {
        setStatus(foutTekst(state.meFout, 'load'), true, isSessieFout(state.meFout) ? loginUrl : '');
      } else {
        setStatus(labels.noCompany || labels.error, true);
      }
      focusStatus();
      return;
    }

    setBusy(true);
    api('POST', '/cases', { company: state.company, variant_gid: variantGid, reden: reden })
      .then(function (res) {
        if (res.status === 201 && res.data && res.data.case) {
          var c = res.data.case;
          if (form) form.reset();
          /* Client-side invoegen: een verse GET /cases zou de nieuwe case nog
             kunnen missen (zoekindex). Wisselt het jaar, dan de lijst herladen. */
          if (c.jaar === state.jaar) {
            rowsEl.insertBefore(renderRow(c), rowsEl.firstChild);
            if (emptyEl) emptyEl.hidden = true;
            if (table) table.hidden = false;
          } else {
            state.jaar = c.jaar;
            fillYears();
            loadCases();
          }
          setStatus(labels.success.replace('[nummer]', c.casenummer));
          return;
        }
        setStatus(foutTekst(res, 'submit'), true, isSessieFout(res) ? loginUrl : '');
      })
      .catch(function () {
        setStatus(labels.error, true);
      })
      .then(function () {
        setBusy(false);
        focusStatus();
      });
  }

  function onSave(tr) {
    /* Theme Editor: nooit netwerk. De voorbeeldrijen hebben id '0', dus zonder
       deze afslag zou Opslaan een echte PATCH op /cases/0 doen */
    if (designMode) {
      setStatus(labels.saved.replace('[nummer]', tr.dataset.caseNummer || ''));
      return;
    }
    if (state.bezig) return;
    var check = tr.querySelector('[data-service-done]');
    var orderInput = tr.querySelector('[data-service-order]');
    var id = tr.dataset.caseId || '';
    if (!id) return;
    var body = {
      afgehandeld: !!(check && check.checked),
      ordernummer: orderInput ? orderInput.value.trim() : ''
    };
    var pad = '/cases/' + encodeURIComponent(id);

    /* alleenRijen: bij een save blijft de primaire knop "Case indienen" heten
       en blijven het productveld en de reden bruikbaar */
    setBusy(true, true);
    api('PATCH', pad, body)
      .then(function (res) {
        /* Laat de app proxy PATCH niet door, dan is POST op hetzelfde pad identiek */
        if (res.status === 405) return api('POST', pad, body);
        return res;
      })
      .then(function (res) {
        if (res.ok && res.data && res.data.case) {
          var nieuw = renderRow(res.data.case);
          if (tr.parentNode) tr.parentNode.replaceChild(nieuw, tr);
          state.wisselBevestigd = false;
          setStatus(labels.saved.replace('[nummer]', res.data.case.casenummer));
          return;
        }
        setStatus(foutTekst(res, 'save'), true, isSessieFout(res) ? loginUrl : '');
      })
      .catch(function () {
        setStatus(labels.error, true);
      })
      .then(function () {
        setBusy(false, true);
        focusStatus();
      });
  }

  /**
   * Jaar- of bedrijfswissel. Staan er onopgeslagen wijzigingen open, dan wordt
   * de eerste wissel teruggedraaid met een melding; wisselt de gebruiker
   * nogmaals, dan worden de wijzigingen bewust verworpen.
   */
  function onWissel(select, veld) {
    if (!select) return;
    if (heeftDirtyRijen() && !state.wisselBevestigd) {
      select.value = veld === 'jaar' ? String(state.jaar) : state.company;
      state.wisselBevestigd = true;
      /* Terugval op labels.error: zonder tekst zou de wissel zonder uitleg worden teruggedraaid */
      setStatus(labels.unsaved || labels.error, true);
      return;
    }
    state.wisselBevestigd = false;
    if (veld === 'jaar') {
      state.jaar = parseInt(select.value, 10) || state.jaar;
    } else {
      state.company = select.value;
      var optie = select.options[select.selectedIndex];
      setAdminCompanyName(optie ? optie.textContent : '');
    }
    loadCases();
  }

  /* ---- Init ---- */

  fillYears();

  if (yearSelect) {
    yearSelect.addEventListener('change', function () {
      onWissel(yearSelect, 'jaar');
    });
  }
  if (companySelect) {
    companySelect.addEventListener('change', function () {
      onWissel(companySelect, 'company');
    });
  }
  if (form) form.addEventListener('submit', onSubmit);

  /* Browsers herstellen formulierwaarden op verschillende momenten en vuren
     daarbij geen change-events: de jaarkeuze na pageshow opnieuw opbouwen */
  window.addEventListener('pageshow', function () {
    fillYears();
  });

  if (designMode) {
    /* Theme Editor: elk rijtype tonen zonder netwerkverkeer, zodat alle
       varianten (afgehandeld, open, admin) te stylen zijn */
    if (adminBlok) adminBlok.hidden = false;
    if (companySelect) {
      companySelect.textContent = '';
      var voorbeeldOptie = document.createElement('option');
      voorbeeldOptie.value = 'voorbeeld';
      voorbeeldOptie.textContent = 'Voorbeeldbedrijf';
      companySelect.appendChild(voorbeeldOptie);
    }
    setAdminCompanyName('Voorbeeldbedrijf');
    renderRows(voorbeeldCases);
    state.rol = 'admin';
    rowsEl.appendChild(renderRow(voorbeeldCases[1]));
  } else {
    /* aria-busy gelijktrekken met de statische laadtoestand uit de sectie: de
       Liquid rendert de laadtekst en verbergt de tabel, maar kan geen aria-busy
       zetten die blijft hangen als het JS nooit draait. Elk eindpad zet hem
       weer op false (toonGeenCases of loadCases). */
    setLoading(true);
    /* Nooit onvoorwaardelijk loadCases: bij een klant zonder bedrijf zou een
       GET /cases zonder company de "geen bedrijf"-melding overschrijven */
    loadMe().then(function () {
      if (state.company) return loadCases();
    });
  }
})();
